import React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { areDisplayAnimationsEnabled } from "./displayAnimationConfig";
import {
  getDisplayTransitionPreset,
  getDisplayTransitionPresetForCategory,
  getDisplayTransitionPresetForStage,
  isStaticDisplayTransition,
} from "./displayTransitions";

function getStageKey(displayStage, transitionKey) {
  const keySuffix =
    transitionKey === null || transitionKey === undefined
      ? ""
      : `-${String(transitionKey)}`;

  if (displayStage === null || displayStage === undefined) {
    return `display-stage-unknown${keySuffix}`;
  }

  return `display-stage-${String(displayStage)}${keySuffix}`;
}

function getDurationScale(durationScale) {
  if (
    typeof durationScale === "number" &&
    Number.isFinite(durationScale) &&
    durationScale > 0
  ) {
    return durationScale;
  }

  return 1;
}

const TRANSITION_TIMING_KEYS = new Set([
  "duration",
  "delay",
  "repeatDelay",
  "delayChildren",
  "staggerChildren",
]);

export function scaleTransitionDuration(transition, durationScale) {
  if (!transition || typeof transition !== "object") {
    return transition;
  }

  if (Array.isArray(transition)) {
    return transition.map((transitionStep) =>
      scaleTransitionDuration(transitionStep, durationScale)
    );
  }

  const scaledTransition = { ...transition };

  Object.keys(scaledTransition).forEach((transitionKey) => {
    const transitionValue = scaledTransition[transitionKey];

    if (TRANSITION_TIMING_KEYS.has(transitionKey)) {
      if (typeof transitionValue === "number") {
        scaledTransition[transitionKey] = transitionValue * durationScale;
      }
      return;
    }

    if (
      transitionKey !== "ease" &&
      transitionValue &&
      typeof transitionValue === "object"
    ) {
      scaledTransition[transitionKey] = scaleTransitionDuration(
        transitionValue,
        durationScale
      );
    }
  });

  return scaledTransition;
}

function getScaledTransitionPreset(transitionPreset, durationScale) {
  const normalizedDurationScale = getDurationScale(durationScale);

  if (normalizedDurationScale === 1 || !transitionPreset) {
    return transitionPreset;
  }

  return {
    ...transitionPreset,
    transition: scaleTransitionDuration(
      transitionPreset.transition,
      normalizedDurationScale
    ),
  };
}

export default function GameStageTransition({
  children,
  uiState,
  displayStage,
  preset,
  category,
  initial = false,
  style,
  durationScale = 1,
  transitionKey,
}) {
  const shouldReduceMotion = useReducedMotion();
  const childArray = React.Children.toArray(children);

  if (!areDisplayAnimationsEnabled() || uiState !== "game") {
    return <>{children}</>;
  }

  const transitionPreset = preset
    ? getDisplayTransitionPreset(preset, { reducedMotion: shouldReduceMotion })
    : getDisplayTransitionPresetForStage(displayStage, {
        reducedMotion: shouldReduceMotion,
      }) ||
      getDisplayTransitionPresetForCategory(category, {
        reducedMotion: shouldReduceMotion,
      });

  if (!transitionPreset) {
    return <>{children}</>;
  }

  const normalizedDurationScale = getDurationScale(durationScale);
  const stageKey = getStageKey(displayStage, transitionKey);
  const wrapperProps = {
    className: "game-stage-transition",
    "data-testid": "game-stage-transition",
    "data-display-stage":
      displayStage === null || displayStage === undefined
        ? undefined
        : String(displayStage),
    "data-display-transition-preset": preset || undefined,
    "data-display-transition-key":
      transitionKey === null || transitionKey === undefined
        ? undefined
        : String(transitionKey),
    "data-display-transition-duration-scale":
      normalizedDurationScale === 1
        ? undefined
        : String(normalizedDurationScale),
    style: { width: "100%", ...style },
  };

  // A stationary transition has no exit worth waiting for, so it does not go
  // through AnimatePresence. In mode="wait" AnimatePresence holds the next
  // stage back until the current one reports its exit, and it can lose that
  // report when two stage changes land a few milliseconds apart - which is
  // how `ad-break-end` and the next round's `next-question-intro` arrive. The
  // display then showed the round intro for the rest of the game while the
  // banner, outside this wrapper, moved on. Keying the wrapper by stage still
  // remounts each stage, so the text fade replays exactly as before.
  if (isStaticDisplayTransition(transitionPreset)) {
    return childArray.length > 0 ? (
      <div key={stageKey} {...wrapperProps}>
        {children}
      </div>
    ) : null;
  }

  const scaledTransitionPreset = getScaledTransitionPreset(
    transitionPreset,
    durationScale
  );

  return (
    <AnimatePresence initial={initial} mode="wait">
      {childArray.length > 0 ? (
        <motion.div key={stageKey} {...wrapperProps} {...scaledTransitionPreset}>
          {children}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
